import { useQuery } from "react-query";
import { SortScope } from "spraypaint";
import Job, { JobStatus, JobType } from "../apiResources/Job";
import JobsClose from "../apiResources/JobsClose";
import { JobClosedCategoryKey } from "../types/job";
import { PaginationQuery } from "./types";

export const fetchJobById = async (jobId: string) =>
  (await Job.find(jobId)).data as Job;

export const closeJob = async (params: {
  jobId: string;
  closedReasonCategory: JobClosedCategoryKey;
  closedReason: string;
}) => {
  const close = new JobsClose({ ...params, jobId: Number(params.jobId) });
  await close.save();

  return close;
};

type JobRelations = "employer" | "currency" | "sector";
type JobExtras = "applicationStats";

type FetchJobQuery = {
  jobId: string;
  includes?: JobRelations[];
  extras?: JobExtras[];
};

const fetchJob = async ({ jobId, includes, extras }: FetchJobQuery) => {
  if (!jobId) {
    return null;
  }

  const response = await Job.includes(includes).selectExtra(extras).find(jobId);

  return response.data as Job;
};

export const useJob = (queryParams: FetchJobQuery) => {
  return useQuery(["job", queryParams], () => fetchJob(queryParams));
};

export const useJobPreview = (jobId: string) => {
  const params: FetchJobQuery = {
    jobId,
    includes: ["employer", "currency", "sector"],
    extras: ["applicationStats"],
  };

  return useQuery(["jobPreview", jobId], () => fetchJob(params));
};

interface FetchJobsQuery extends PaginationQuery {
  status?: JobStatus | JobStatus[];
  jobType?: JobType | JobType[];
  query?: string;
  includes?: JobRelations[];
  extras?: JobExtras[];
  order?: string | SortScope;
  sector: string | string[];
  currency: string | string[];
  isEngagedByCurrentUser?: boolean;
  onHold?: boolean;
}

const fetchJobs = async (queryParams: FetchJobsQuery) => {
  const {
    page,
    per,
    status,
    jobType,
    query,
    includes,
    order,
    extras,
    sector,
    currency,
    isEngagedByCurrentUser,
    onHold,
  } = queryParams;

  let search: string | string[] = query;

  if (typeof query === "string") {
    search = query.split(",");
  }

  if (Array.isArray(search)) {
    search = (search as unknown as string[])
      .filter((s) => s)
      .map((s) => s.trim());
  }

  const response = await Job.where({
    by_title_location_sector_or_type: { match: search },
    engaged_by_current_user: isEngagedByCurrentUser,
    status: status,
    jobType,
    parent_sector: sector,
    currency_id: currency,
    on_hold: onHold,
  })
    .includes(includes)
    .selectExtra(extras)
    .page(page)
    .per(per)
    .order(order)
    .stats({ total: "count" })
    .all();

  return {
    jobs: response.data as Job[],
    meta: {
      total: response.meta.stats.total.count,
    },
  };
};

export const useJobSearch = (queryParams: FetchJobsQuery) => {
  const status =
    queryParams.status.length === 0
      ? [JobStatus.NEW, JobStatus.OPEN, JobStatus.INTERVIEW, JobStatus.OFFER]
      : queryParams.status;

  const params: FetchJobsQuery = {
    ...queryParams,
    isEngagedByCurrentUser: false,
    onHold: false,
    status,
    includes: ["sector", "currency", "employer"],
  };

  return useQuery(["jobs", queryParams], () => fetchJobs(params), {
    keepPreviousData: true,
  });
};
